/**
 * Consolidation Module
 *
 * Post-scan cleanup and multi-signal duplicate page detection.
 * Extracted from scannedBookService.ts (v8.74).
 *
 * Responsibilities:
 * - Detect garbage OCR, fragments, empty pages
 * - Multi-signal page similarity (text, illustration, page type)
 * - Union-Find clustering for duplicate detection
 * - Client-side consolidation with Jaccard similarity
 */

import { supabase } from '@/lib/supabase/client';
import { Sentry } from '@/lib/sentry';
import type { ScannedBookPage } from './types';
import { getBookPages } from './page-extraction';

// ============================================================================
// Types
// ============================================================================

export interface PostScanCleanupResult {
  bookId: string;
  totalPages: number;
  issues: {
    duplicates: Array<{ pageNumber: number; duplicateOf: number; similarity: number }>;
    garbage: Array<{ pageNumber: number; reason: string }>;
    fragments: Array<{ pageNumber: number; reason: string }>;
    emptyPages: number[];
  };
  pagesNeedingReview: number[];
  cleanupApplied: boolean;
}

export interface ConsolidationResult {
  /** Page numbers that are the "best" capture for each logical page */
  consolidatedPages: number[];
  /** Page numbers that were superseded by better captures */
  supersededPages: number[];
  /** Details about each cluster found */
  clusters: Array<{
    pages: number[];
    bestPage: number;
    reason: string;
  }>;
}

interface PageSimilarity {
  page1: number;
  page2: number;
  score: number;
  signals: {
    pageNumber: number;
    textSimilarity: number;
    illustrationSimilarity: number;
    pageTypeMatch: number;
  };
}

// ============================================================================
// Private Helpers
// ============================================================================

/**
 * Check if text appears to be garbage/artifact OCR
 */
function isGarbageOCR(text: string): { isGarbage: boolean; reason: string | null } {
  if (!text || text.trim().length === 0) {
    return { isGarbage: false, reason: null }; // Empty is handled separately
  }

  const trimmed = text.trim();

  // Check 1: Keyboard characters (scanning keyboard by accident)
  const keyboardPattern = /[qwerty]{4,}|[asdf]{4,}|[zxcv]{4,}|[hjkl]{4,}/i;
  if (keyboardPattern.test(trimmed)) {
    return { isGarbage: true, reason: 'keyboard_characters' };
  }

  // Check 2: Excessive special characters (>30% non-alphanumeric)
  const alphanumeric = trimmed.replace(/[^a-zA-Z0-9\s]/g, '');
  if (alphanumeric.length < trimmed.length * 0.7) {
    return { isGarbage: true, reason: 'excessive_special_chars' };
  }

  // Check 3: Garbled OCR (many non-words in sequence)
  const words = trimmed.split(/\s+/).filter(w => w.length >= 3);
  if (words.length >= 5) {
    const commonEndings = /(?:ing|tion|ed|er|ly|es|ful|ness|ment|able|ible|ous|ive)$/i;
    const commonWords = /^(?:the|and|for|are|but|not|you|all|can|her|was|one|our|day|had|his|how|its|let|may|old|see|too|way|who|boy|did|get|has|him|into|made|many|some|them|then|these|this|two|what|when|will|with|have|from|they|been|call|come|down|each|find|first|go|he|I|if|in|is|it|just|know|like|little|long|look|make|more|my|no|now|of|on|only|or|other|over|said|she|so|than|that|there|thing|think|time|up|us|very|want|water|we|were|your)$/i;

    let realWordCount = 0;
    for (const word of words) {
      const clean = word.replace(/[^a-zA-Z]/g, '').toLowerCase();
      if (commonEndings.test(clean) || commonWords.test(clean) || clean.length <= 3) {
        realWordCount++;
      }
    }

    if (realWordCount / words.length < 0.4) {
      return { isGarbage: true, reason: 'garbled_ocr' };
    }
  }

  // Check 4: Single repeated character patterns
  if (/(.)\1{5,}/.test(trimmed)) {
    return { isGarbage: true, reason: 'repeated_characters' };
  }

  return { isGarbage: false, reason: null };
}

/**
 * Check if text is a fragment (too short to be meaningful content)
 */
function isFragmentText(text: string, pageType: string): { isFragment: boolean; reason: string | null } {
  if (!text) return { isFragment: false, reason: null };

  const trimmed = text.trim();
  const wordCount = trimmed.split(/\s+/).filter(w => w.length > 0).length;

  if (/^\d{1,3}$/.test(trimmed)) {
    return { isFragment: false, reason: null };
  }

  if (['title', 'copyright', 'dedication', 'half_title'].includes(pageType)) {
    return { isFragment: false, reason: null };
  }

  if (wordCount < 3 && pageType === 'text') {
    return { isFragment: true, reason: 'too_short' };
  }

  if (trimmed.length < 10 && wordCount < 3) {
    return { isFragment: true, reason: 'minimal_content' };
  }

  return { isFragment: false, reason: null };
}

/**
 * Get the effective page type for consolidation.
 * Uses GPT-4o classification (page_type) when available, falls back to regex heuristics.
 */
function getEffectivePageType(page: ScannedBookPage): 'story' | 'front_matter' | 'back_matter' | 'illustration_only' {
  const classifiedType = page.page_type as string | null;
  if (classifiedType && ['story', 'text', 'front_matter', 'back_matter', 'illustration_only', 'illustration', 'copyright'].includes(classifiedType)) {
    if (classifiedType === 'text') return 'story';
    if (classifiedType === 'illustration') return 'illustration_only';
    if (classifiedType === 'copyright') return 'front_matter';
    return classifiedType as 'story' | 'front_matter' | 'back_matter' | 'illustration_only';
  }

  const text = page.extracted_text?.toLowerCase() || '';
  const frontMatterIndicators = [
    'copyright', 'isbn', 'library of congress', 'all rights reserved',
    'published by', 'printed in', 'manufactured in', 'cataloging-in-publication'
  ];
  const backMatterIndicators = ['about the author', 'bibliography', 'glossary', 'index'];

  if (frontMatterIndicators.some(ind => text.includes(ind))) return 'front_matter';
  if (backMatterIndicators.some(ind => text.includes(ind))) return 'back_matter';

  return 'story';
}

/**
 * Compute similarity score between two pages using multiple signals.
 */
function computePageSimilarity(p1: ScannedBookPage, p2: ScannedBookPage): PageSimilarity {
  const signals = {
    pageNumber: 0,
    textSimilarity: 0,
    illustrationSimilarity: 0,
    pageTypeMatch: 0,
  };

  // Signal 1: Detected page number (definitive when available)
  if (p1.detected_page_number !== null && p2.detected_page_number !== null) {
    if (p1.detected_page_number === p2.detected_page_number) {
      return { page1: p1.page_number, page2: p2.page_number, score: 100, signals: { ...signals, pageNumber: 100 } };
    } else {
      return { page1: p1.page_number, page2: p2.page_number, score: 0, signals };
    }
  }

  // Signal 2: Text similarity
  const text1 = (p1.extracted_text || '').toLowerCase().trim();
  const text2 = (p2.extracted_text || '').toLowerCase().trim();

  if (text1.length > 20 && text2.length > 20) {
    const words1 = new Set(text1.split(/\s+/).filter(w => w.length > 2));
    const words2 = new Set(text2.split(/\s+/).filter(w => w.length > 2));

    if (words1.size > 0 && words2.size > 0) {
      const intersection = new Set([...words1].filter(w => words2.has(w)));
      const union = new Set([...words1, ...words2]);
      const jaccard = intersection.size / union.size;

      const containment = Math.max(
        text2.includes(text1.slice(0, Math.min(50, text1.length))) ? 0.8 : 0,
        text1.includes(text2.slice(0, Math.min(50, text2.length))) ? 0.8 : 0
      );

      signals.textSimilarity = Math.round(Math.max(jaccard, containment) * 100);
    }
  } else if (text1.length < 20 && text2.length < 20) {
    signals.textSimilarity = 30;
  }

  // Signal 3: Illustration description similarity
  const desc1 = (p1.illustration_description || '').toLowerCase();
  const desc2 = (p2.illustration_description || '').toLowerCase();

  if (desc1.length > 10 && desc2.length > 10) {
    const stopWords = new Set(['the', 'a', 'an', 'is', 'are', 'was', 'were', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'and', 'or', 'that', 'this', 'it', 'as', 'by', 'be', 'has', 'have', 'from']);

    const extractKeywords = (text: string) => new Set(
      text.split(/\s+/)
        .map(w => w.replace(/[^a-z]/g, ''))
        .filter(w => w.length > 3 && !stopWords.has(w))
    );

    const kw1 = extractKeywords(desc1);
    const kw2 = extractKeywords(desc2);

    if (kw1.size > 0 && kw2.size > 0) {
      const kwIntersection = new Set([...kw1].filter(w => kw2.has(w)));
      const kwUnion = new Set([...kw1, ...kw2]);
      signals.illustrationSimilarity = Math.round((kwIntersection.size / kwUnion.size) * 100);
    }
  }

  // Signal 4: Page type match
  signals.pageTypeMatch = p1.page_type === p2.page_type ? 100 : 0;

  // Weighted combination
  const score = Math.round(
    signals.textSimilarity * 0.50 +
    signals.illustrationSimilarity * 0.35 +
    signals.pageTypeMatch * 0.15
  );

  return { page1: p1.page_number, page2: p2.page_number, score, signals };
}

/**
 * Compute quality score for a capture to determine "best" in a cluster.
 */
function computeCaptureQualityScore(page: ScannedBookPage): number {
  let score = 0;

  score += (page.extraction_confidence || 0) * 10;

  const wordCount = page.extracted_text?.split(/\s+/).filter(w => w.length > 0).length || 0;
  score += Math.min(wordCount, 100);

  const issueCount = page.quality_issues?.length || 0;
  score -= issueCount * 20;

  const captureQuality = (page as any).capture_quality as string | undefined;
  const qualityBonus: Record<string, number> = {
    excellent: 50, good: 30, fair: 10, poor: -20, invalid: -50
  };
  score += qualityBonus[captureQuality || 'fair'] || 0;

  if (page.illustration_description) score += 10;
  if (!page.needs_rescan) score += 15;

  return score;
}

/**
 * Build clusters of similar pages using Union-Find algorithm.
 */
function buildPageClusters(pages: ScannedBookPage[], similarities: PageSimilarity[]): ScannedBookPage[][] {
  const parent = new Map<number, number>();
  pages.forEach(p => parent.set(p.page_number, p.page_number));

  const find = (x: number): number => {
    if (parent.get(x) !== x) {
      parent.set(x, find(parent.get(x)!));
    }
    return parent.get(x)!;
  };

  const union = (x: number, y: number) => {
    const px = find(x);
    const py = find(y);
    if (px !== py) {
      parent.set(px, py);
    }
  };

  for (const sim of similarities) {
    union(sim.page1, sim.page2);
  }

  const clusters = new Map<number, ScannedBookPage[]>();
  for (const page of pages) {
    const root = find(page.page_number);
    if (!clusters.has(root)) {
      clusters.set(root, []);
    }
    clusters.get(root)!.push(page);
  }

  return [...clusters.values()];
}

/**
 * Compute Jaccard similarity between two word sets
 */
export function computeJaccardSimilarity(text1: string, text2: string): number {
  const extractWords = (text: string): Set<string> => {
    return new Set(
      text.toLowerCase()
        .replace(/[^a-z'\s-]/g, '')
        .split(/\s+/)
        .filter(w => w.length >= 3)
    );
  };

  const words1 = extractWords(text1);
  const words2 = extractWords(text2);

  if (words1.size === 0 || words2.size === 0) return 0;

  const intersection = new Set([...words1].filter(w => words2.has(w)));
  const union = new Set([...words1, ...words2]);

  return intersection.size / union.size;
}

// ============================================================================
// Exported Functions
// ============================================================================

/**
 * Run post-scan cleanup pass on a book.
 *
 * Called AFTER all OCR is complete to:
 * 1. Consolidate duplicate captures (multi-signal similarity detection)
 * 2. Detect garbage OCR (keyboard, artifacts, garbled text)
 * 3. Detect fragments (partial captures, too short)
 * 4. Flag pages needing review
 */
export async function runPostScanCleanup(
  bookId: string,
  options: { autoFlagForRescan?: boolean } = {}
): Promise<PostScanCleanupResult> {
  const { autoFlagForRescan = true } = options;

  console.log(`[PostScanCleanup] Starting cleanup pass for book ${bookId}`);

  const pages = await getBookPages(bookId);

  if (pages.length === 0) {
    return {
      bookId,
      totalPages: 0,
      issues: { duplicates: [], garbage: [], fragments: [], emptyPages: [] },
      pagesNeedingReview: [],
      cleanupApplied: false,
    };
  }

  const issues: PostScanCleanupResult['issues'] = {
    duplicates: [],
    garbage: [],
    fragments: [],
    emptyPages: [],
  };

  const pagesNeedingReview: number[] = [];
  const pageTexts = new Map<number, string>();

  // First pass: collect texts and detect per-page issues
  for (const page of pages) {
    const text = page.extracted_text || '';
    pageTexts.set(page.page_number, text);

    if (!text.trim()) {
      if (!page.has_illustration) {
        issues.emptyPages.push(page.page_number);
        pagesNeedingReview.push(page.page_number);
      }
      continue;
    }

    const garbageCheck = isGarbageOCR(text);
    if (garbageCheck.isGarbage) {
      issues.garbage.push({ pageNumber: page.page_number, reason: garbageCheck.reason! });
      pagesNeedingReview.push(page.page_number);
      continue;
    }

    const fragmentCheck = isFragmentText(text, page.page_type || 'text');
    if (fragmentCheck.isFragment) {
      issues.fragments.push({ pageNumber: page.page_number, reason: fragmentCheck.reason! });
      pagesNeedingReview.push(page.page_number);
    }
  }

  // Second pass: detect duplicates (compare all pairs)
  const processedPages = pages.filter(p => pageTexts.get(p.page_number)?.trim());

  for (let i = 0; i < processedPages.length; i++) {
    const page1 = processedPages[i];
    const text1 = pageTexts.get(page1.page_number) || '';

    if (text1.trim().length < 20) continue;

    for (let j = i + 1; j < processedPages.length; j++) {
      const page2 = processedPages[j];
      const text2 = pageTexts.get(page2.page_number) || '';

      if (text2.trim().length < 20) continue;

      const similarity = computeJaccardSimilarity(text1, text2);

      if (similarity > 0.6) {
        issues.duplicates.push({
          pageNumber: page2.page_number,
          duplicateOf: page1.page_number,
          similarity: Math.round(similarity * 100),
        });

        if (!pagesNeedingReview.includes(page2.page_number)) {
          pagesNeedingReview.push(page2.page_number);
        }
      }
    }
  }

  console.log(`[PostScanCleanup] Book ${bookId}: ${pages.length} pages analyzed`);
  console.log(`[PostScanCleanup] Issues found: ${issues.duplicates.length} duplicates, ${issues.garbage.length} garbage, ${issues.fragments.length} fragments, ${issues.emptyPages.length} empty`);

  if (autoFlagForRescan && pagesNeedingReview.length > 0) {
    const uniquePages = [...new Set(pagesNeedingReview)];

    for (const pageNumber of uniquePages) {
      let reason = 'Post-scan cleanup flagged for review';

      const duplicate = issues.duplicates.find(d => d.pageNumber === pageNumber);
      if (duplicate) {
        reason = `Likely duplicate of page ${duplicate.duplicateOf} (${duplicate.similarity}% similar)`;
      }

      const garbage = issues.garbage.find(g => g.pageNumber === pageNumber);
      if (garbage) {
        reason = `Garbage OCR detected: ${garbage.reason}`;
      }

      const fragment = issues.fragments.find(f => f.pageNumber === pageNumber);
      if (fragment) {
        reason = `Fragment text: ${fragment.reason}`;
      }

      if (issues.emptyPages.includes(pageNumber)) {
        reason = 'Empty page with no illustration';
      }

      await supabase
        .from('scanned_book_pages')
        .update({
          quality_issues: [reason],
          needs_rescan: false,
        } as any)
        .eq('book_id', bookId)
        .eq('page_number', pageNumber);
    }

    console.log(`[PostScanCleanup] Flagged ${uniquePages.length} pages with quality issues`);
  }

  // Run multi-signal consolidation
  console.log(`[PostScanCleanup] Running page consolidation...`);
  const consolidationResult = await consolidateBookPages(bookId);

  if (consolidationResult.supersededPages.length > 0) {
    console.log(`[PostScanCleanup] Consolidation superseded ${consolidationResult.supersededPages.length} pages`);
  }

  return {
    bookId,
    totalPages: pages.length,
    issues,
    pagesNeedingReview: [...new Set(pagesNeedingReview)].sort((a, b) => a - b),
    cleanupApplied: autoFlagForRescan && pagesNeedingReview.length > 0,
  };
}

/**
 * Consolidate book pages using multi-signal similarity detection.
 * Identifies duplicate captures and selects the best one per logical page.
 */
export async function consolidateBookPages(bookId: string): Promise<ConsolidationResult> {
  const pages = await getBookPages(bookId);

  console.log(`[Consolidation] Starting consolidation for book ${bookId} with ${pages.length} pages`);

  if (pages.length <= 1) {
    return {
      consolidatedPages: pages.map(p => p.page_number),
      supersededPages: [],
      clusters: pages.map(p => ({ pages: [p.page_number], bestPage: p.page_number, reason: 'single_page' })),
    };
  }

  // Categorize pages by effective page type
  const pagesByType = new Map<string, ScannedBookPage[]>();
  for (const page of pages) {
    const effectiveType = getEffectivePageType(page);
    if (!pagesByType.has(effectiveType)) {
      pagesByType.set(effectiveType, []);
    }
    pagesByType.get(effectiveType)!.push(page);
  }

  const typeCounts = Array.from(pagesByType.entries()).map(([type, p]) => `${type}:${p.length}`).join(', ');
  console.log(`[Consolidation] Page types: ${typeCounts}`);

  const allSuperseded: number[] = [];
  const allConsolidated: number[] = [];
  const allClusterDetails: ConsolidationResult['clusters'] = [];

  // Front matter: aggressively consolidate
  const frontMatterPages = pagesByType.get('front_matter') || [];
  if (frontMatterPages.length > 0) {
    const sortedFM = [...frontMatterPages].sort((a, b) => a.page_number - b.page_number);
    allConsolidated.push(sortedFM[0].page_number);
    if (sortedFM.length > 1) {
      const superseded = sortedFM.slice(1).map(p => p.page_number);
      allSuperseded.push(...superseded);
      allClusterDetails.push({
        pages: sortedFM.map(p => p.page_number),
        bestPage: sortedFM[0].page_number,
        reason: 'front_matter_consolidated',
      });
      console.log(`[Consolidation] Front matter: keeping ${sortedFM[0].page_number}, superseding [${superseded.join(', ')}]`);
    }
  }

  // Back matter: aggressively consolidate
  const backMatterPages = pagesByType.get('back_matter') || [];
  if (backMatterPages.length > 0) {
    const sortedBM = [...backMatterPages].sort((a, b) => a.page_number - b.page_number);
    allConsolidated.push(sortedBM[0].page_number);
    if (sortedBM.length > 1) {
      const superseded = sortedBM.slice(1).map(p => p.page_number);
      allSuperseded.push(...superseded);
      allClusterDetails.push({
        pages: sortedBM.map(p => p.page_number),
        bestPage: sortedBM[0].page_number,
        reason: 'back_matter_consolidated',
      });
      console.log(`[Consolidation] Back matter: keeping ${sortedBM[0].page_number}, superseding [${superseded.join(', ')}]`);
    }
  }

  // Story + Illustration pages: only consolidate TRUE duplicates
  const contentPages = [
    ...(pagesByType.get('story') || []),
    ...(pagesByType.get('illustration_only') || [])
  ];

  console.log(`[Consolidation] Processing ${contentPages.length} content pages for duplicate detection`);

  const similarities: PageSimilarity[] = [];
  const SIMILARITY_THRESHOLD = 85;

  for (let i = 0; i < contentPages.length; i++) {
    for (let j = i + 1; j < contentPages.length; j++) {
      const sim = computePageSimilarity(contentPages[i], contentPages[j]);
      if (sim.score >= SIMILARITY_THRESHOLD) {
        similarities.push(sim);
        console.log(`[Consolidation] Similar content: page ${sim.page1} <-> page ${sim.page2} (score=${sim.score}, text=${sim.signals.textSimilarity})`);
      }
    }
  }

  const clusters = buildPageClusters(contentPages, similarities);

  console.log(`[Consolidation] Found ${clusters.length} content clusters from ${contentPages.length} content pages`);

  for (const cluster of clusters) {
    if (cluster.length === 1) {
      allConsolidated.push(cluster[0].page_number);
      allClusterDetails.push({
        pages: [cluster[0].page_number],
        bestPage: cluster[0].page_number,
        reason: 'unique_page',
      });
      continue;
    }

    const scored = cluster
      .map(p => ({ page: p, score: computeCaptureQualityScore(p) }))
      .sort((a, b) => b.score - a.score);

    const bestPage = scored[0].page.page_number;
    allConsolidated.push(bestPage);

    const superseded = scored.slice(1).map(s => s.page.page_number);
    allSuperseded.push(...superseded);

    allClusterDetails.push({
      pages: cluster.map(p => p.page_number),
      bestPage,
      reason: `best_of_${cluster.length}_captures`,
    });

    console.log(`[Consolidation] Cluster [${cluster.map(p => p.page_number).join(', ')}] -> best: ${bestPage} (superseded: ${superseded.join(', ') || 'none'})`);
  }

  // Mark superseded pages in database
  if (allSuperseded.length > 0) {
    const { error } = await supabase
      .from('scanned_book_pages')
      .update({ is_superseded: true } as any)
      .eq('book_id', bookId)
      .in('page_number', allSuperseded);

    if (error) {
      console.error(`[Consolidation] Failed to mark superseded pages:`, error);
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { flow: 'scan_pipeline', step: 'consolidation' },
      });
    } else {
      console.log(`[Consolidation] Marked ${allSuperseded.length} pages as superseded`);
    }
  }

  if (allConsolidated.length > 0) {
    await supabase
      .from('scanned_book_pages')
      .update({ is_superseded: false } as any)
      .eq('book_id', bookId)
      .in('page_number', allConsolidated);
  }

  console.log(`[Consolidation] Complete: ${allConsolidated.length} consolidated, ${allSuperseded.length} superseded`);

  return {
    consolidatedPages: allConsolidated.sort((a, b) => a - b),
    supersededPages: allSuperseded.sort((a, b) => a - b),
    clusters: allClusterDetails,
  };
}

/**
 * Run client-side consolidation with multi-pass duplicate detection.
 *
 * Pass 1: Exact match by detected_page_number or text hash
 * Pass 2: Fuzzy match by Jaccard similarity (catches near-duplicates)
 */
export async function runClientSideConsolidation(bookId: string): Promise<void> {
  try {
    const { data: incomplete } = await supabase
      .from('scanned_book_pages')
      .select('page_number')
      .eq('book_id', bookId)
      .in('processing_status', ['pending', 'processing'])
      .limit(1);

    if (incomplete && incomplete.length > 0) {
      return;
    }

    const { data: pages, error } = await supabase
      .from('scanned_book_pages')
      .select('page_number, extracted_text, detected_page_number, extraction_confidence')
      .eq('book_id', bookId)
      .eq('processing_status', 'complete')
      .eq('is_superseded', false)
      .order('page_number');

    if (error || !pages || pages.length <= 1) {
      await supabase
        .from('scanned_books')
        .update({ scan_completed: true })
        .eq('id', bookId);
      return;
    }

    console.log(`[Consolidation] Processing ${pages.length} pages for book ${bookId}`);

    const supersededPages: Set<number> = new Set();

    // Pass 1: Exact match by detected_page_number or text hash
    const clusters = new Map<string, typeof pages>();

    for (const page of pages) {
      let clusterKey: string;

      if (page.detected_page_number !== null) {
        clusterKey = `dpn:${page.detected_page_number}`;
      } else {
        const text = (page.extracted_text || '').toLowerCase().replace(/\s+/g, ' ').trim().slice(0, 200);
        let hash = 0;
        for (let i = 0; i < text.length; i++) {
          hash = ((hash << 5) - hash) + text.charCodeAt(i);
          hash = hash & hash;
        }
        clusterKey = `txt:${hash.toString(16)}`;
      }

      if (!clusters.has(clusterKey)) {
        clusters.set(clusterKey, []);
      }
      clusters.get(clusterKey)!.push(page);
    }

    for (const [, clusterPages] of clusters) {
      if (clusterPages.length <= 1) continue;

      clusterPages.sort((a, b) => {
        const confDiff = (b.extraction_confidence || 0) - (a.extraction_confidence || 0);
        if (confDiff !== 0) return confDiff;
        return (b.extracted_text?.length || 0) - (a.extracted_text?.length || 0);
      });

      for (let i = 1; i < clusterPages.length; i++) {
        supersededPages.add(clusterPages[i].page_number);
      }
    }

    console.log(`[Consolidation] Pass 1 (hash): Found ${supersededPages.size} duplicates`);

    // Pass 2: Fuzzy match by Jaccard similarity
    const SIMILARITY_THRESHOLD = 0.92;
    const LENGTH_RATIO_MIN = 0.6;
    const remainingPages = pages.filter(p => !supersededPages.has(p.page_number));

    for (let i = 0; i < remainingPages.length; i++) {
      const pageA = remainingPages[i];
      if (supersededPages.has(pageA.page_number)) continue;

      const textA = pageA.extracted_text || '';
      if (textA.length < 20) continue;

      for (let j = i + 1; j < remainingPages.length; j++) {
        const pageB = remainingPages[j];
        if (supersededPages.has(pageB.page_number)) continue;

        const textB = pageB.extracted_text || '';
        if (textB.length < 20) continue;

        const lengthRatio = Math.min(textA.length, textB.length) / Math.max(textA.length, textB.length);
        if (lengthRatio < LENGTH_RATIO_MIN) {
          continue;
        }

        const similarity = computeJaccardSimilarity(textA, textB);

        if (similarity >= SIMILARITY_THRESHOLD) {
          const [shorter, longer] = textA.length < textB.length
            ? [textA.toLowerCase(), textB.toLowerCase()]
            : [textB.toLowerCase(), textA.toLowerCase()];

          const shorterWords = shorter.split(/\s+/).filter(w => w.length >= 3);
          const containedWords = shorterWords.filter(w => longer.includes(w));
          const containmentRatio = shorterWords.length > 0
            ? containedWords.length / shorterWords.length
            : 0;

          if (containmentRatio < 0.80) {
            console.log(`[Consolidation] Pass 2: Pages ${pageA.page_number} <-> ${pageB.page_number} similar (${(similarity * 100).toFixed(0)}%) but low containment (${(containmentRatio * 100).toFixed(0)}%), keeping both`);
            continue;
          }

          const aScore = (pageA.extraction_confidence || 0) * 100 + (textA.length / 10);
          const bScore = (pageB.extraction_confidence || 0) * 100 + (textB.length / 10);

          const loser = aScore >= bScore ? pageB : pageA;
          supersededPages.add(loser.page_number);

          console.log(`[Consolidation] Pass 2: Pages ${pageA.page_number} <-> ${pageB.page_number} similarity=${(similarity * 100).toFixed(0)}%, containment=${(containmentRatio * 100).toFixed(0)}%, superseding ${loser.page_number}`);
        }
      }
    }

    console.log(`[Consolidation] Pass 2 (Jaccard): Total ${supersededPages.size} duplicates`);

    // Apply supersessions
    if (supersededPages.size > 0) {
      const { error: updateError } = await supabase
        .from('scanned_book_pages')
        .update({ is_superseded: true } as any)
        .eq('book_id', bookId)
        .in('page_number', [...supersededPages]);

      if (updateError) {
        console.error('[Consolidation] Failed to update superseded pages:', updateError);
        Sentry.captureException(updateError instanceof Error ? updateError : new Error(String(updateError)), {
          tags: { flow: 'scan_pipeline', step: 'client_consolidation' },
        });
      } else {
        console.log(`[Consolidation] Marked ${supersededPages.size} pages as superseded`);
      }
    }

    await supabase
      .from('scanned_books')
      .update({ scan_completed: true })
      .eq('id', bookId);

    console.log(`[Consolidation] Book ${bookId} marked as complete`);

  } catch (err) {
    console.error(`[Consolidation] Failed:`, err);
    Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
      tags: { flow: 'scan_pipeline', step: 'client_consolidation' },
    });
  }
}
